import { JetStreamLegendColormap } from "@/layers/geojson/JetStreamsLayerImpl";
import { buildIsoBandLegendItems } from "@/layers/geojson/isoBandsFillUtils";
import { createBinaryAnomalyLegendColormap } from "@/layers/png/binary/BinaryAnomalyStyle";
import { getBinaryAnomalyDefinition, getBinaryAnomalyLegendLabel } from "@/layers/png/binary/TemperatureAnomalyPoc";
import { LayerKindName } from "@/layers/utility/createLayerObject";
import {
  transformAdvancedThresholds,
  transformColorThresholds,
} from "@/overlay/components/CommonParts/Threshold/types";
import type { MapLegendProps } from "@/overlay/components/Legends/LegendsContainer";
import { getThresholdLegendItems } from "@/overlay/components/Legends/ThresholdLegend/utils";
import { isColorMap } from "@/utility/typeGuard";
import { t } from "@lingui/core/macro";
import { useMemo } from "react";

function layerLegendLabel(layer: { label?: unknown }) {
  return typeof layer.label === "string" ? layer.label.trim() || undefined : undefined;
}

export function useMapLegendProps(layers: any[] | undefined): MapLegendProps[] | undefined {
  return useMemo(() => {
    return layers
      ?.filter(
        (layer: any) =>
          (layer.kind === LayerKindName.WmsLayerDescription ||
            layer.kind === LayerKindName.LightningLayerDescription ||
            layer.kind === LayerKindName.GenericPoiLayerDescription ||
            layer.kind === LayerKindName.JetStreamsLayerDescription ||
            layer.kind === LayerKindName.CustomGeoJSONLayerDescription ||
            layer.kind === LayerKindName.IsoBandsLayerDescription) &&
          layer.opacity !== 0 &&
          layer.show,
      )
      .map((layer: any) => {
        const anomalyDefinition = getBinaryAnomalyDefinition(layer);
        if (anomalyDefinition && layer.color_map === anomalyDefinition.style.id) {
          const legendLabel = getBinaryAnomalyLegendLabel(anomalyDefinition);
          return {
            layerId: layer.id,
            parameter: layer.parameter_unit,
            label: layerLegendLabel(layer) ?? legendLabel,
            legendVisible: layer.legend_visible,
            customColormap: { ...createBinaryAnomalyLegendColormap(anomalyDefinition), label: legendLabel },
            legendUnit: anomalyDefinition.unit,
          };
        }

        if (layer.kind === LayerKindName.GenericPoiLayerDescription) {
          const poiLayer = layer as any;
          const legendItems = getThresholdLegendItems(
            poiLayer.poiOptions?.coloringOptions?.thresholds || [],
            poiLayer.parameter_unit?.split(":")[1] || "",
            poiLayer.poiOptions.coloringOptions?.advancedThresholds || [],
          );
          return {
            thresholdItems: legendItems,
            parameter: poiLayer.parameter_unit || "",
            label: layerLegendLabel(poiLayer),
            layerId: poiLayer.id,
            legendVisible: !!poiLayer.legend_visible && legendItems.length > 0,
          };
        }

        if (layer.kind === LayerKindName.CustomGeoJSONLayerDescription) {
          const geojsonLayer = layer as any;
          const legendItems = getThresholdLegendItems(
            transformColorThresholds(geojsonLayer.thresholds),
            geojsonLayer.parameter_unit?.split(":")[1] || "",
            transformAdvancedThresholds(geojsonLayer.advanced_thresholds),
          );
          return {
            thresholdItems: legendItems,
            parameter: geojsonLayer.parameter_unit || "",
            label: layerLegendLabel(geojsonLayer),
            layerId: geojsonLayer.id,
            legendVisible: !!geojsonLayer.legend_visible && legendItems.length > 0,
          };
        }

        if (layer.kind === LayerKindName.JetStreamsLayerDescription) {
          const jetStreamsLayer = layer as any;
          return {
            parameter: "",
            label: layerLegendLabel(jetStreamsLayer),
            layerId: jetStreamsLayer.id,
            legendVisible: !!jetStreamsLayer.legend_visible,
            customColormap: JetStreamLegendColormap,
            legendUnit: "kn",
          };
        }

        if (layer.kind === LayerKindName.IsoBandsLayerDescription) {
          const isoBandsLayer = layer as any;
          const legendItems = buildIsoBandLegendItems({
            values: isoBandsLayer.values,
            valueRange: isoBandsLayer.value_range,
            isobandIntervals: isoBandsLayer.custom_options?.isoband_intervals,
            baseColor: isoBandsLayer.custom_options?.fill_color ?? "#4A90E2",
            fillColors: isoBandsLayer.custom_options?.fill_colors,
            fillColorMode: isoBandsLayer.custom_options?.fill_color_mode,
            fillReverse: isoBandsLayer.custom_options?.fill_reverse,
            unit: isoBandsLayer.parameter_unit?.split(":")[1] || "",
          });

          return {
            thresholdItems: legendItems,
            parameter: isoBandsLayer.parameter_unit || "",
            label: layerLegendLabel(isoBandsLayer),
            layerId: isoBandsLayer.id,
            legendVisible: (isoBandsLayer.legend_visible ?? true) && legendItems.length > 0,
          };
        }

        const colormapLayer = layer as any;
        const colorMapName = colormapLayer.color_map;
        const isBuiltIn = isColorMap(colorMapName);
        const customColormapId =
          typeof colorMapName === "string" && colorMapName.trim() !== "" && !isBuiltIn ? colorMapName : undefined;

        return {
          layerId: colormapLayer.id,
          parameter: colormapLayer.parameter_unit,
          label: layerLegendLabel(colormapLayer),
          legendVisible: colormapLayer.legend_visible,
          style: isBuiltIn ? colorMapName : undefined,
          customColormapId,
        };
      });
  }, [layers]);
}
